import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { search, type SearchHit } from '../api/operations';
import { Icon } from '../lib/icons';

/** Long enough that a single keystroke does not query, short enough to feel live. */
const DEBOUNCE_MS = 200;

/**
 * FEAT-015 — the top-bar search.
 *
 * Which modules appear is entirely the server's answer: a group the caller
 * cannot reach is absent from the response, so there is no filtering here. That
 * matters — a client-side filter would mean the browser had been sent results
 * the user is not entitled to and merely declined to draw them.
 */
export function GlobalSearch(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const query = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => search(debounced),
    enabled: debounced.length > 0,
  });

  // Flattened once so the keyboard can walk the results as one list, which is
  // what a person expects even though they are drawn in groups.
  const flat: SearchHit[] = (query.data ?? []).flatMap((group) => group.hits);

  useEffect(() => {
    if (!isOpen) return;

    function onDocumentMouseDown(event: MouseEvent): void {
      if (!boxRef.current?.contains(event.target as Node)) setIsOpen(false);
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isOpen]);

  function go(hit: SearchHit): void {
    setIsOpen(false);
    setText('');
    navigate(hit.path);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      const hit = flat[activeIndex];
      if (hit) {
        event.preventDefault();
        go(hit);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  const activeId = activeIndex >= 0 && flat[activeIndex] ? `search-opt-${activeIndex}` : undefined;
  let index = -1;

  return (
    <div className="search peoplebox" ref={boxRef}>
      <Icon name="search" />
      <input
        type="search"
        autoComplete="off"
        placeholder={t('search_placeholder')}
        value={text}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="search-menu"
        aria-autocomplete="list"
        aria-label={t('search_placeholder')}
        {...(activeId ? { 'aria-activedescendant': activeId } : {})}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setIsOpen(true);
          setActiveIndex(-1);
        }}
        onKeyDown={onKeyDown}
      />

      <div
        className={isOpen && debounced ? 'people-menu open' : 'people-menu'}
        id="search-menu"
        role="listbox"
      >
        {query.isPending && debounced ? (
          <div className="people-empty">{t('loading')}</div>
        ) : flat.length === 0 ? (
          <div className="people-empty">{t('search_no_results')}</div>
        ) : (
          query.data?.map((group) => (
            <div key={group.module}>
              <div className="search-group">{t(`search_group_${group.module}`)}</div>
              {group.hits.map((hit) => {
                index += 1;
                const optionIndex = index;
                return (
                  <div
                    key={`${hit.module}-${hit.id}`}
                    id={`search-opt-${optionIndex}`}
                    role="option"
                    aria-selected={optionIndex === activeIndex}
                    className={optionIndex === activeIndex ? 'people-opt active' : 'people-opt'}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      go(hit);
                    }}
                    onMouseEnter={() => setActiveIndex(optionIndex)}
                  >
                    <span>
                      <span className="pn">{hit.title}</span>
                      <span className="pco">{hit.subtitle}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
